import { errorToast, successToast } from '@/utilities/toast.utilities';
import { sqStoredTablesApi } from '@/sdk/api/StoredTablesApi';
import {
  resetStoredTable,
  setIsLoadingStoredTable,
  setMaterializedTable,
  setStoredTable,
  setStoredTableName,
  setTableId,
} from '@/storedTableEditor/storedTable.actions';
import type {
  MaterializedTableCell,
  MaterializedTableHeader,
  MaterializedTableOutput,
  MaterializedTableRow,
  ProcessedMaterializedTable,
  ProcessedTableCell,
  TextTableCell,
} from '@/tableDefinitionEditor/tableDefinition.types';
import { type BooleanTableCell, type NumericTableCell } from '@/tableDefinitionEditor/tableDefinition.types';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import type { CellValueChangedEvent, ColDef } from 'ag-grid-enterprise';
import type { StoredTableColumnOutputV1 } from '@/sdk/model/StoredTableColumnOutputV1';
import { ColumnTypeEnum } from '@/sdk/model/StoredTableColumnOutputV1';
import { toNumber } from '@/utilities/numberHelper.utilities';
import _ from 'lodash';
import { filterPropertyAndUomColumns } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import type { StoredTableColumnInputV1 } from '@/sdk/model/StoredTableColumnInputV1';
import { setDisplayStoredTableEditor } from '@/worksheet/worksheet.actions';
import { setStoredTableEditorVisible } from '@/main/layout.actions';
import { getMaterializedTable, updateTableDefinitionName } from '@/tableDefinitionEditor/tableDefinition.utilities';
import type { DatasourceOutputV1 } from '@/sdk/model/DatasourceOutputV1';
import { sqDatasourcesApi } from '@/sdk/api/DatasourcesApi';
import { t } from 'i18next';
import { sqStoredTableStore } from '@/core/core.stores';
import { UOM_COLUMN_MATCH_SUFFIX } from '@/tableDefinitionEditor/tableDefinition.constants';

export const STORED_TABLE_CANCELLATION_GROUP = 'stored-table';

export async function getExistingStoredTable(storedTableId: string) {
  try {
    setIsLoadingStoredTable(true);
    const { data: storedTableData } = await sqStoredTablesApi.getStoredTable({ id: storedTableId });
    setStoredTable(storedTableData);

    const output = await getMaterializedTable(
      storedTableId,
      storedTableData.columns.map((column) => column.columnName),
      [],
      STORED_TABLE_CANCELLATION_GROUP,
    );
    const newTable: MaterializedTableOutput | undefined = output.data?.table;
    setMaterializedTable(newTable);
    setIsLoadingStoredTable(false);

    return storedTableData;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

export function editStoredTable(storedTableId: string) {
  resetStoredTable();
  setTableId(storedTableId);
}

export const buildRowDataForAgGrid = (
  agGridColumnDefinitions: ColDef[],
  materializedTable: ProcessedMaterializedTable | undefined,
) => {
  if (!materializedTable) {
    return null;
  }

  const materializedTableHeaders = materializedTable.headers;

  const rows = materializedTable.rows;
  return rows.map((row) => {
    const rowData: Record<string, any> = {};
    materializedTableHeaders.forEach((header: any, index: number) => {
      const agColumn = agGridColumnDefinitions.find((columnDef) => columnDef.headerName === header.name);
      const field = agColumn?.field;
      if (field) {
        rowData[field] = row[index];
      }
    });
    return rowData;
  });
};

export const handleCellValueChanged = async (
  event: CellValueChangedEvent,
  columns: StoredTableColumnOutputV1[],
  tableId: string | undefined,
) => {
  const uniqueIdColumn = columns.find((c) => c.columnName === SeeqNames.MaterializedTables.UniqueRowId)?.id;
  const column = columns.find((c) => c.columnName === event.colDef.headerName);

  if (column && uniqueIdColumn) {
    const overrideValue = event.data[column.id].value || event.data[column.id].uuid;
    const rowIdValue = event.data[uniqueIdColumn]?.value;
    // TODO CRAB-54280 apply overrides to stored tables when supported in the backend
  } else {
    console.error('Column field is not defined or Unique Row Id column is not defined.');
  }
  successToast({ messageKey: 'STORED_TABLES.VALUE_UPDATED' });
  event.api.clearFocusedCell();
};

export const processMaterializedTable = (
  rawMaterializedTable: MaterializedTableOutput | undefined,
  columns: StoredTableColumnOutputV1[],
): ProcessedMaterializedTable | undefined => {
  if (!rawMaterializedTable) {
    return undefined;
  }
  const rows = rawMaterializedTable.rows;
  const headers = rawMaterializedTable.headers;

  const processedRows: ProcessedTableCell[][] = rows.map((row) => {
    return mapRowToProcessedCells(row, headers, columns);
  });

  return filterPropertyAndUomColumns(headers, processedRows, rawMaterializedTable.hasMore);
};

const mapRowToProcessedCells = (
  row: MaterializedTableRow,
  headers: MaterializedTableHeader[],
  columns: StoredTableColumnOutputV1[],
): ProcessedTableCell[] => {
  return row.map((cellValue, columnIndex) => {
    const header = headers[columnIndex];
    const column = columns.find((col) => col.columnName === header.name);

    return {
      override: false,
      ...processCellByType({ cellValue, row, header, headers, column }),
    };
  });
};

const processCellByType = (processCellProps: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  header: MaterializedTableHeader;
  headers: MaterializedTableHeader[];
  column: StoredTableColumnOutputV1 | undefined;
}) => {
  if (_.isNil(processCellProps.cellValue)) {
    return {
      value: undefined,
    };
  }

  switch (processCellProps.header.type) {
    case ColumnTypeEnum.NUMERIC:
      return processNumericCell(processCellProps);
    case ColumnTypeEnum.BOOLEAN:
      return processBooleanCell(processCellProps.cellValue);
    default: {
      return processDefaultCell(processCellProps.cellValue);
    }
  }
};

const processDefaultCell = (cellValue: MaterializedTableCell) => {
  return {
    value: cellValue ? cellValue.toString() : undefined,
  } as TextTableCell;
};

const processNumericCell = ({
  cellValue,
  row,
  header,
  headers,
  column,
}: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  header: MaterializedTableHeader;
  headers: MaterializedTableHeader[];
  column: StoredTableColumnOutputV1 | undefined;
}) => {
  const headerMap = headers.reduce((map, header) => {
    map.set(header.name, header);
    return map;
  }, new Map<string, MaterializedTableHeader>());

  const potentialNumericValue = toNumber(cellValue);
  const potentialUomColumn = headerMap.get(`${header.name}${UOM_COLUMN_MATCH_SUFFIX}`);
  const potentialUomColumnIndex = headers.findIndex((h) => h.name === potentialUomColumn?.name);
  const potentialUomColumnValue = potentialUomColumnIndex > -1 ? row[potentialUomColumnIndex] : undefined;
  // If there was not a uom column, then check the column definition for a uom override
  const uomColumnValue = potentialUomColumnValue ?? column?.columnUom;

  if (_.isNil(potentialNumericValue)) {
    return processDefaultCell(cellValue);
  } else {
    return {
      value: potentialNumericValue,
      uom: uomColumnValue as string | undefined,
    } as Partial<NumericTableCell>;
  }
};

const processBooleanCell = (cellValue: MaterializedTableCell) => {
  return {
    value: !!cellValue,
  } as Partial<BooleanTableCell>;
};

export const onTableReload = (newTable?: MaterializedTableOutput) => {
  setMaterializedTable(newTable);
};

export const storedTableColumnOutputToInput = (column: StoredTableColumnOutputV1): StoredTableColumnInputV1 => {
  return {
    columnType: column.columnType,
    columnUom: column.columnUom,
    columnName: column.columnName,
    isIndexed: column.isIndexed,
    isHidden: column.isHidden,
    sortAscending: column.sortAscending,
    sortIndex: column.sortIndex,
    description: column.description,
  };
};

export const closeStoredTableEditor = () => {
  setDisplayStoredTableEditor(false);
  setStoredTableEditorVisible(false);
};

export const deleteStoredTableColumn = async (column: StoredTableColumnOutputV1, tableId: string) => {
  try {
    await sqStoredTablesApi.deleteColumnFromStoredTable({ id: tableId, columnId: column.id });
    await getExistingStoredTable(tableId);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Retrieves the data source for a table definition.
 */
export const getStoredTableDataSource = async (storedTableId: string): Promise<DatasourceOutputV1 | undefined> => {
  const {
    data: { datasources },
  } = await sqDatasourcesApi.getDatasources({
    datasourceId: storedTableId.toLocaleLowerCase(),
  });

  return datasources?.at(0);
};

export const handleUpdateTableName = async (newName: string | undefined, tableId: string | undefined) => {
  const trimmedTableName = newName?.trim();
  if (trimmedTableName) {
    if (tableId && trimmedTableName !== sqStoredTableStore.name) {
      try {
        await updateTableDefinitionName({ tableDefinitionId: tableId, name: trimmedTableName });
        setStoredTableName(trimmedTableName);
      } catch (e) {
        errorToast({ messageKey: t('ITEM_FINDER.UPDATE_NAME_TABLE_DEFINITION_ERROR') });
      }
    }
  }
};

export const deleteStoredTableRows = async (tableId: string, rowIds: string[]) => {
  try {
    const response = await sqStoredTablesApi.deleteStoredTableRows(
      {
        rowIds,
      },
      {
        tableId,
      },
    );
    return response.data;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return { deletedRowCount: 0 };
  }
};
